import { styled, css, useTheme } from '@vebox/etui-system';
import '../utilities/format-ms.js';
import { createTransition } from '../utilities/create-transition.js';
import { createElement } from 'react';
import { getStyleProp } from '../utilities/get-styleprop.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, Button as Button$2, Span } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import '../Icon/icons.js';
import '../Icon/helper.js';
import { lighten, darken } from '@vebox/etui-colors';
import '../ClickOutside/ClickOutside.js';
import Tooltip from '../Tooltip/Tooltip.js';

const ButtonFrontComponent = styled(Div)`
  width: 24px;
  height: 24px;
  margin: auto;
`;

const _excluded = ["block", "disabled", "small", "large", "shape", "theme", "iconOnly"];
const variants = (variations = null) => props => {
  const {
    variant
  } = props;
  if (variations && typeof variant === 'string') {
    return css`
        ${variations[variant] || ''}
      `;
  }
  return '';
};
const Button = styled(Button$2).attrs(_ref => {
  let {
      block,
      disabled,
      small,
      large,
      shape,
      theme,
      iconOnly
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2(_objectSpread2({}, theme.typography.button), {}, {
    position: 'relative',
    display: block ? 'flex' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    border: 'none',
    width: block ? '100%' : 'auto',
    userSelect: 'none',
    fontSize: small ? theme.typography.pxToRem(13) : large ? theme.typography.pxToRem(15) : theme.typography.button.fontSize,
    p: small ? iconOnly ? `${theme.spacing.sm}px ${theme.spacing.sm}px` : `${theme.spacing.xs}px ${theme.spacing.sm}px` : large ? iconOnly ? `${theme.spacing.lg}px ${theme.spacing.lg}px` : `${theme.spacing.md}px ${theme.spacing.lg}px` : iconOnly ? `${theme.spacing.md}px ${theme.spacing.md}px` : `${theme.spacing.sm}px ${theme.spacing.md}px`,
    borderRadius: shape === 'circle' ? '50%' : '2px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    bg: 'transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    transition: createTransition(['color', 'background-color'], {
      duration: 'shorter'
    })
  }, rest);
})`
  ${variants({
  default: ({
    theme,
    active,
    pointerEvents,
    getHoverStyles,
    getActiveStyles
  }) => css`
      color: ${theme.palette.text.secondary};
      pointer-events: ${pointerEvents};

      :disabled {
        color: ${lighten(theme.palette.text.primary, 0.75)};
      }
      ${active ? '&,' : '' /* Forces hover state if the active prop is truthy */}
      :hover:not([disabled]) {
        color: ${theme.palette.text.primary};
        ${getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()}
      }
      :active:not([disabled]) {
        color: ${lighten(theme.palette.text.primary, theme.palette.tonalOffset)};
        ${getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()}
      }
      :focus-visible:not([disabled]) {
        outline: 2px solid ${theme.palette.text.secondary};
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
      }
    `,
  strong: ({
    theme,
    active,
    pointerEvents,
    getHoverStyles,
    getActiveStyles
  }) => css`
      color: ${theme.palette.common.white};
      pointer-events: ${pointerEvents};
      background-color: ${theme.palette.brand.light};

      :disabled {
        background-color: ${lighten(theme.palette.brand.light, 0.75)};
      }
      ${active ? '&,' : '' /* Forces hover state if the active prop is truthy */}
      :hover:not([disabled]) {
        background-color: ${darken(theme.palette.brand.light, theme.palette.tonalOffset)};
        ${getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()}
      }
      :active:not([disabled]) {
        background-color: ${darken(theme.palette.brand.light, theme.palette.tonalOffset + 0.1)};
        ${getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()}
      }
      :focus-visible:not([disabled]) {
        outline: 2px solid
          ${darken(theme.palette.brand.light, theme.palette.tonalOffset)};
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
      }
    `,
  primary: ({
    theme,
    active,
    pointerEvents,
    getHoverStyles,
    getActiveStyles
  }) => css`
      color: ${theme.palette.common.white};
      pointer-events: ${pointerEvents};
      background-color: ${theme.palette.primary.main};

      :disabled {
        background-color: ${lighten(theme.palette.primary.main, 0.75)};
      }
      ${active ? '&,' : '' /* Forces hover state if the active prop is truthy */}
      :hover:not([disabled]) {
        background-color: ${darken(theme.palette.primary.main, theme.palette.tonalOffset)};
        ${getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()}
      }
      :active:not([disabled]) {
        background-color: ${lighten(theme.palette.primary.main, 0.05)};
        ${getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()}
      }
      :focus-visible:not([disabled]) {
        outline: 2px solid
          ${darken(theme.palette.primary.main, theme.palette.tonalOffset)};
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
      }
    `,
  secondary: ({
    theme,
    active,
    pointerEvents,
    getHoverStyles,
    getActiveStyles
  }) => css`
      color: ${theme.palette.common.white};
      pointer-events: ${pointerEvents};
      background-color: ${theme.palette.secondary.main};

      :disabled {
        background-color: ${lighten(theme.palette.secondary.main, 0.75)};
      }
      ${active ? '&,' : '' /* Forces hover state if the active prop is truthy */}
      :hover:not([disabled]) {
        background-color: ${darken(theme.palette.secondary.main, theme.palette.tonalOffset)};
        ${getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()}
      }
      :active:not([disabled]) {
        background-color: ${darken(theme.palette.secondary.main, 0.3)};
        ${getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()}
      }
      :focus-visible:not([disabled]) {
        outline: 2px solid
          ${darken(theme.palette.secondary.main, theme.palette.tonalOffset)};
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
      }
    `,
  tertiary: ({
    theme,
    active,
    pointerEvents,
    getHoverStyles,
    getActiveStyles
  }) => css`
      color: ${theme.palette.text.secondary};
      pointer-events: ${pointerEvents};
      background-color: transparent;

      :disabled {
        color: ${lighten(theme.palette.text.primary, 0.75)};
      }
      ${active ? '&,' : '' /* Forces hover state if the active prop is truthy */}
      :hover:not([disabled]) {
        color: ${theme.palette.text.primary};
        background-color: ${theme.palette.action.hover};
        ${getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()}
      }
      :active:not([disabled]) {
        background-color: ${darken(theme.palette.action.hover, 0.05)};
        ${getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()}
      }
      :focus-visible:not([disabled]) {
        outline: 2px solid ${theme.palette.text.secondary};
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
      }
    `,
  'tertiary-outlined': ({
    theme,
    active,
    pointerEvents,
    getHoverStyles,
    getActiveStyles
  }) => css`
      border: 2px solid;
      border-color: ${theme.palette.text.primary};
      color: ${theme.palette.text.primary};
      pointer-events: ${pointerEvents};
      background-color: transparent;

      :disabled {
        border-color: ${lighten(theme.palette.text.primary, 0.75)};
        color: ${lighten(theme.palette.text.primary, 0.75)};
      }
      ${active ? '&,' : '' /* Forces hover state if the active prop is truthy */}
      :hover:not([disabled]) {
        background-color: ${darken(theme.palette.action.hover, 0.05)};
        ${getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()}
      }
      :active:not([disabled]) {
        background-color: ${darken(theme.palette.action.hover, 0.1)};
        ${getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()}
      }
      :focus-visible:not([disabled]) {
        outline: 2px solid ${theme.palette.text.secondary};
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
      }
    `
})}
  :hover:not([disabled]) {
    ${({
  getHoverStyles
}) => getHoverStyles === null || getHoverStyles === void 0 ? void 0 : getHoverStyles()};
  }
  :active:not([disabled]) {
    ${({
  getActiveStyles
}) => getActiveStyles === null || getActiveStyles === void 0 ? void 0 : getActiveStyles()};
  }
`;

const ButtonTextSpan = styled(Span)`
  display: flex;
  align-items: center;

  ${({
  hasIcon,
  theme
}) => hasIcon && css`
      margin-left: ${theme.spacing.xxs}px;
    `};

  ${({
  textCenter
}) => textCenter && css`
      width: 100%;
      align-items: center;
      justify-content: center;
    `};
`;

const _excluded$1 = ["type", "shape", "variant", "onClick", "textCenter", "children", "iconProps", "frontComponent", "styles", "toolTip"];
const Button$1 = _ref => {
  let {
      type = 'button',
      shape = 'default',
      variant = 'default',
      onClick,
      textCenter,
      children,
      iconProps,
      frontComponent,
      styles = {},
      toolTip
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  const theme = useTheme();
  const handleClick = event => {
    if (onClick) {
      onClick(event);
    }
  };
  let otherProps = _objectSpread2({
    type,
    iconOnly: !!(!children && iconProps),
    shape,
    variant
  }, props);
  const iconNode = iconProps ? /*#__PURE__*/createElement(Icon, _extends({
    weight: "400"
  }, iconProps)) : null;
  const componentNode = frontComponent ? /*#__PURE__*/createElement(ButtonFrontComponent, null, frontComponent) : null;
  const getStyles = key => {
    return getStyleProp(key, styles, theme);
  };
  const pointerEvents = props !== null && props !== void 0 && props.disabled && toolTip ? 'none' : 'auto';
  const getButton = () => {
    otherProps = _objectSpread2(_objectSpread2({}, otherProps), {}, {
      pointerEvents
    });
    return /*#__PURE__*/createElement(Button, _extends({
      onClick: handleClick
    }, otherProps, getStyles('root')), iconNode, componentNode, !otherProps.iconOnly && /*#__PURE__*/createElement(ButtonTextSpan, _extends({
      hasIcon: iconProps !== undefined || frontComponent !== undefined,
      textCenter: textCenter
    }, getStyles('text')), children));
  };
  if (toolTip) {
    var _toolTip$styles, _toolTip$styles2;
    const tooltipStyles = _objectSpread2(_objectSpread2({}, toolTip === null || toolTip === void 0 ? void 0 : toolTip.styles), {}, {
      root: _objectSpread2({
        width: '100%'
      }, toolTip === null || toolTip === void 0 ? void 0 : (_toolTip$styles = toolTip.styles) === null || _toolTip$styles === void 0 ? void 0 : _toolTip$styles.root),
      div: _objectSpread2({
        cursor: props.disabled && 'not-allowed'
      }, toolTip === null || toolTip === void 0 ? void 0 : (_toolTip$styles2 = toolTip.styles) === null || _toolTip$styles2 === void 0 ? void 0 : _toolTip$styles2.div)
    });
    return /*#__PURE__*/createElement(Tooltip, _extends({
      disableToolTip: (toolTip === null || toolTip === void 0 ? void 0 : toolTip.message) === undefined
    }, toolTip, {
      styles: tooltipStyles
    }), getButton());
  }
  return getButton();
};

export default Button$1;
